import {StorageProvider} from "../../gd/const/storage";
import {AttachmentSystemType} from "./@types";
import ApiAttachment from "../../gd/api/attachment";

export default class Attachment {

    public id                 : ID  // this is NOT id field in DB attachments - we use it here for collection
    public fileId             : ID
    public fileType           : string // ?
    public isFlagged          : boolean

    public systemType         : AttachmentSystemType    // type of attachment is it saved in attachments db table or custom

    public companyId          : ID      // in suport tickets we have attachments from other company (!) here we use gd.companyID as default

    public storageProvider    : any // enum needed

    public name               : string // filenamne
    public mime               : string // mime type
    public size               : number // in bytes

    // preview status ... 0 unknown 1 no preview 2 has preview (thumbnail)
    public preview            : number // _.get(info, 'preview', gd.const.attachment.preview.UNKNOWN)

    public dropzoneFile       : any // we store link to dropzone upload file here
    public dropzoneUploadDone : boolean | null // true when all uplaoded and processed

    public isSaved            : boolean  // indicates that files has been saved in DB and moved to company folder

    public inMessageRTF       : boolean // alwsy calculate dynamically I guess

    // Files specific - usually emopty
    public projectId          : ID | null
    public taskId             : ID | null
    public createdByUserId    : ID
    public momentCreated      : Moment

    public renderMode         : any  // very specific param used in notes to redefine how attchments look inside of the editor (!)
    public refAttachmentManager  : any  // sometimes passed

    public _uploadUrl         : any // used when upload via dropbox


    constructor(info:any) {

        this.id                 = info.fileId // we need it for collection

        this.systemType         = info.systemType || AttachmentSystemType.DEFAULT

        this.fileId             = info.fileId
        this.fileType           = info.fileType
        this.isFlagged          = info.isFlagged // go figure why I guess in files ?

        this.storageProvider    = info.storageProvider

        this.name               = info.name // filename
        this.mime               = info.mime
        this.size               = info.size // in bytes

        // preview status ... 0 unknown 1 no preview 2 has preview (thumbnail)
        this.preview            = _.get(info, 'preview', gd.const.attachment.preview.UNKNOWN)

        this.dropzoneFile       = _.get(info, 'dropzoneFile', null) // we store link to dropzone upload file here
        this.dropzoneUploadDone = _.get(info, 'dropzoneUploadDone', null) // true when all uplaoded and processed

        this.isSaved            = _.get(info, 'isSaved', true)  // indicates that files has been saved in DB and moved to company folder

        this.inMessageRTF       = false // alwsy calculate dynamically I guess

        // Files specific - usually emopty
        this.projectId          = info.projectId
        this.taskId             = info.taskId
        this.createdByUserId    = info.createdByUserId
        this.momentCreated      = info.momentCreated

        this.renderMode         = null  // very specific param used in notes to redefine how attchments look inside of the editor (!)
        this.refAttachmentManager  = null  // sometimes passed

        this._uploadUrl         = null // used when upload via dropbox

        this.companyId          = gd.companyId


        if (_.isNil(this.storageProvider)) {
            // console.error("#djd934 attachemnt no storage provider")
            // this is a patch for inserted into rich editor ... without storageprovider data
            this.storageProvider = StorageProvider.AMAZON_DEFAULT
        }

        if (!this.fileType) {
            // console.warn("not fileType specified")
            this.fileType = this._autoDetectFileType()
            // console.warn("   init by extension ->", this.fileType)
        }

        // google drive preview patch ?
        // I think it might be something for old onew from june 2022 we calculate it (!)
        if (this.isGoogleDrive && !_.has(info, 'preview')) this.preview = gd.const.attachment.preview.AVAILABLE

    }

    static initForNewUpload(dropzoneFile:any) {

        const attachment = new Attachment({
            fileId              : gd.utils.generateUUID(),
            storageProvider     : gd.company.settings.storage.provider,
            name                : dropzoneFile.name,

            mime                : dropzoneFile.type,
            size                : dropzoneFile.size,

            dropzoneFile        : dropzoneFile,
            dropzoneUploadDone  : false,
            isSaved             : false
        })

        return attachment
    }

    static initFromGoogleDrive(googleDriveFile:any) {

        // preview: {
        //     UNKNOWN: 0,
        //         AVAILABLE: 1,
        //         NOT_AVAILABLE: 2,
        // }

        // now we check if preview exists
        // use number ... cant read gd.const here :( gd.const.attachment.preview.AVAILABLE
        const preview = (_.isEmpty(googleDriveFile.thumbnailLink))?2:1

        const attachment = new Attachment({
            fileId              : googleDriveFile.fileId,
            storageProvider     : StorageProvider.GOOGLE_DRIVE,
            name                : googleDriveFile.fileName,

            mime                : googleDriveFile.mimeType,
            size                : googleDriveFile.size,
            isSaved             : true,
            preview             : preview
        })

        return attachment
    }

    get isNewUpload() {
        return !!this.dropzoneFile
    }

    get isUploadDone() {
        if (!this.isNewUpload) return true
        return this.dropzoneFile.status === 'success'
    }

    // used to separate images and other files into groups
    get typeGroup() {
        return [gd.const.attachment.fileType.IMAGE, gd.const.attachment.fileType.GRAPHIC_SOURCE].includes(this.fileType) ? "image" : "file";
    }

    get isGoogleDrive() {
        return this.storageProvider === StorageProvider.GOOGLE_DRIVE
    }

    get fileSizeFormatted() {
        return gd.utils.getFileSize(this.size)
    }

    get fileExtension() {

        if (!this.name) {
            console.warn("No file name specified")
            return null
        }

        const fileParts = this.name.split(".")
        const extension = _.last(fileParts)
        return extension
    }

    get canGenerateThumbnail() {
        return [
            gd.const.attachment.fileType.IMAGE,
            gd.const.attachment.fileType.GRAPHIC_SOURCE
        ].includes(this.fileType)
    }

    get previewIcon() {

        switch (this.fileType) {
            case gd.const.attachment.fileType.IMAGE:            return 'ii5-file-image';
            case gd.const.attachment.fileType.DOCUMENT:         return 'i5-file-text';
            case gd.const.attachment.fileType.ARCHIVE:          return 'i5-file-archive';
            case gd.const.attachment.fileType.SOURCE_CODE:      return 'i5-file-code';
            case gd.const.attachment.fileType.VIDEO:            return 'ii5-file-video';
            case gd.const.attachment.fileType.AUDIO:            return 'ii5-file-audio';
            case gd.const.attachment.fileType.GRAPHIC_SOURCE:   return 'i5-file-image';
            default:
                return 'i5-file'
        }
    }

    // can open preview app
    get previewAvailable() {

        if (this.preview === gd.const.attachment.preview.AVAILABLE) return true

        if (this.isNewUpload) {
            if ([gd.const.attachment.fileType.IMAGE, gd.const.attachment.fileType.GRAPHIC_SOURCE].includes(this.fileType)) return true
            else return false
        }

        return false
    }


    _autoDetectFileType() {
        if (this.mime) {
            const typeByMime = this._getTypeByMime()
            if (typeByMime!==null) return typeByMime
        }

        return this._getFileTypeByExtension()

    }

    _getTypeByMime() {

        if (this.mime.includes('image/'))
            return gd.const.attachment.fileType.IMAGE

        if (_.values(gd.const.attachment.googleDriveMimes).includes(this.mime))
            return gd.const.attachment.fileType.GOOGLE_DOCUMENT;

        return null
        // else
        //     res = gd.const.attachment.fileType.OTHER;

    }

    _getFileTypeByExtension() {
        switch (this.fileExtension) {

            case 'png':
            case 'gif':
            case 'jpeg':
            case 'jpg':
                return gd.const.attachment.fileType.IMAGE

            case 'js':
                return gd.const.attachment.fileType.SOURCE_CODE

            default:
                return gd.const.attachment.fileType.OTHER

        }

        // OTHER: 0,
        //     IMAGE: 1,
        //     DOCUMENT: 2,
        //     GOOGLE_DOCUMENT: 12,
        //     WEB: 3,
        //     ARCHIVE: 4,
        //     SOURCE_CODE: 5,
        //     VIDEO: 6,
        //     AUDIO: 7,
        //     THREED: 8,
        //     GRAPHIC_SOURCE: 9,
        //     FONT: 10,
        //     EBOOK: 11


    }

    toggleIsFlagded() {

        this.isFlagged = !this.isFlagged

        gd.api.Project.toggleFileStarred(this.projectId!, this.fileId, this.isFlagged);

        return this.isFlagged

    }

    thumbnailUrl(size=160) {

        let thumbnailSize = null;

        switch (size) {
            case 1200:
                thumbnailSize = 1200;
                break;
            case 160:
            default:
                thumbnailSize = 160
                break;
        }

        const folder = this.isSaved ? this.companyId + "/" : 'temp/'

        switch (this.storageProvider) {

            case StorageProvider.AMAZON_DEV:
            case StorageProvider.AMAZON_NORTH_AMERICA:
            case StorageProvider.AMAZON_SOUTH_AMERICA:
            case StorageProvider.AMAZON_EUROPE:
            case StorageProvider.AMAZON_ASIA:
                // @ts-ignore
                return StorageProvider[this.storageProvider].httpUrl + folder + this.fileId + "/"+thumbnailSize+".jpg"
                // return "https://s3-sa-east-1-goodday-work.s3.sa-east-1.amazonaws.com/" + folder + this.fileId + "/"+thumbnailSize+".jpg"

            case StorageProvider.GOOGLE_DRIVE:
                return "https://drive.google.com/thumbnail?authuser=0&sz=w220&id="+this.fileId;

            case StorageProvider.AMAZON_DEFAULT:
            case StorageProvider.LOCAL:
            default:

                if (thumbnailSize === 160) return gd.api.Attachment.preview160Url(this.fileId)
                if (thumbnailSize === 1200) return gd.api.Attachment.preview1200Url(this.fileId)

        }

    }

    downloadUrl() {

        switch (this.storageProvider) {

            // case StorageProvider.AMAZON_DEV:
                // tut problem with download headers as file... content disposition - so we do redicret at backend
                // return "https://s3-sa-east-1-goodday-work.s3.sa-east-1.amazonaws.com/"+gd.companyId+"/" + this.fileId + "/original"

            case StorageProvider.GOOGLE_DRIVE:
                return "https://drive.google.com/uc?export=download&id=" + this.fileId;

            case StorageProvider.AMAZON_DEV:
            case StorageProvider.AMAZON_NORTH_AMERICA:
            case StorageProvider.AMAZON_SOUTH_AMERICA:
            case StorageProvider.AMAZON_EUROPE:
            case StorageProvider.AMAZON_ASIA:
            case StorageProvider.AMAZON_DEFAULT:
            case StorageProvider.LOCAL:
            default:
                return gd.api.Attachment.downloadUrl(this.fileId)
        }

    }

    download() {
        let win;

        win = window.open(this.downloadUrl());

        if (win) {
            //Browser has allowed it to be opened
            win.focus();
        }
    }

    openInBrowser() {

        let viewUrl;

        switch (this.storageProvider) {

            case StorageProvider.GOOGLE_DRIVE:

                const isGoogleDoc = this.fileType === gd.const.attachment.fileType.GOOGLE_DOCUMENT;
                if (isGoogleDoc) {
                    switch(this.mime) {
                        case 'application/vnd.google-apps.document':        viewUrl = "https://docs.google.com/document/d/"+this.fileId+"/edit"; break;
                        case 'application/vnd.google-apps.spreadsheet':     viewUrl = "https://docs.google.com/spreadsheets/d/"+this.fileId+"/edit"; break;
                        case 'application/vnd.google-apps.drawing':         viewUrl = "https://docs.google.com/drawings/d/"+this.fileId+"/edit"; break;
                        case 'application/vnd.google-apps.form':            viewUrl = "https://docs.google.com/forms/d/"+this.fileId+"/edit"; break;
                        case 'application/vnd.google-apps.presentation':    viewUrl = "https://docs.google.com/presentation/d/"+this.fileId+"/edit"; break;
                        default:                                            viewUrl = "https://drive.google.com/file/d/"+this.fileId+"/view?usp=drivesdk";
                    }
                } else {
                    viewUrl = "https://drive.google.com/file/d/"+this.fileId+"/view?usp=drivesdk";
                }

                break;

            case StorageProvider.AMAZON_DEV:
            case StorageProvider.AMAZON_NORTH_AMERICA:
            case StorageProvider.AMAZON_SOUTH_AMERICA:
            case StorageProvider.AMAZON_EUROPE:
            case StorageProvider.AMAZON_ASIA:

                // @ts-ignore
                viewUrl = StorageProvider[this.storageProvider].httpUrl + this.companyId + "/" + this.fileId + "/original"
                // viewUrl = "https://s3-sa-east-1-goodday-work.s3.sa-east-1.amazonaws.com/"+gd.companyId+"/" + this.fileId + "/original"

                break;

            case StorageProvider.AMAZON_DEFAULT:
            case StorageProvider.LOCAL:
            default:

                viewUrl = gd.api.Attachment.openUrl(this.fileId);

                break

        }

        window.open(viewUrl);

        // https://s3-sa-east-1-goodday-work.s3.sa-east-1.amazonaws.com/89d5pB/9f165244-dcab-4a47-942c-1253b17404c0/original

    }

    async openByFileObject(){
        const url = await ApiAttachment.open(this).promise()

        window.open(url)
    }

    async downloadByFileObject(){
        const url = await ApiAttachment.downloadByFileObject(this).promise()
        let win;

        win = window.open(url);

        if (win) {
            //Browser has allowed it to be opened
            win.focus();
        }
    }

    async delete(){
        await ApiAttachment.delete([this]).promise()
    }


    toJSON() {
        return {
            fileId          : this.fileId,
            name            : this.name,
            systemType      : this.systemType,
            fileName        : this.name,    // temp patch to make for with old api
            storageProvider : this.storageProvider,
            mime            : this.mime,
            fileType        : this.fileType,
            size            : this.size,
            preview         : this.preview,
            // isSaved         : this.isSaved  //need it because in notes we can switch between pages without saving and reinit collection
        }
    }
}
